/**
 * The Legend tab.
 *
 * WHAT WAS WRONG
 * This tab asked GeoServer for a `GetLegendGraphic` image for EVERY layer,
 * whatever its source:
 *
 *     <WmsLegend wmsUrl={geoServerUrl} layerName={layer.name} />
 *
 * A file-imported layer has no GeoServer layer of that name — often no
 * GeoServer at all — and an ArcGIS layer belongs to an entirely different
 * service. Both produced a broken image, which `WmsLegend` hides on error, so
 * the tab showed the layer's name and nothing beneath it. Applying symbology
 * changed nothing either: the app's own renderer never reached that request.
 *
 * WHERE THE SYMBOLS COME FROM NOW
 * `collectLegend` — the module the PRINTED legend has always used, which
 * already knows the rule: the app's own `renderer` wins whenever it exists,
 * because that is what drew the map; otherwise the service that styled the
 * layer is asked (ArcGIS `/legend`, GeoServer `GetLegendGraphic`).
 *
 * So the tab and the printed sheet are now the same answer rendered twice —
 * canvas on paper, DOM on screen — rather than two ideas that could disagree.
 *
 * Point swatches are built with `buildSymbolSvg`, the same function the map's
 * own marker icon uses, so the key matches the map exactly rather than
 * approximately.
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getLineDashArray, getPointSymbol, buildSymbolSvg } from '../../layers/symbols';
import { collectLegend, type LegendGroup, type LegendSwatch } from '../../utils/legendSymbols';
import type { Layer } from '../../types';

const SWATCH_WIDTH = 34;
const SWATCH_HEIGHT = 20;

/** One symbol, drawn the way the map draws it. */
function SwatchView({ swatch }: { swatch: LegendSwatch }) {
  const box = {
    width: SWATCH_WIDTH,
    height: SWATCH_HEIGHT,
    // `pale` swatches are near-white — an Esri road casing, say — and would be
    // invisible against the panel. The same problem the print sheet solves by
    // putting something behind them.
    ...(swatch.pale ? { background: '#E2E8F0', borderRadius: 3 } : {}),
  } as const;

  if (swatch.image) {
    return (
      <span style={box} className="flex flex-shrink-0 items-center justify-start overflow-hidden">
        <img
          src={swatch.image.src}
          alt=""
          className="max-h-full max-w-full object-contain object-left"
        />
      </span>
    );
  }

  if (swatch.symbol) {
    const symbol = swatch.symbol;

    if (symbol.geometry === 'point') {
      const definition = getPointSymbol(symbol.symbolId);
      // Capped so a large point symbol cannot push the row out of line, the
      // same trade the printed swatch makes.
      const size = Math.min(SWATCH_HEIGHT, Math.max(8, symbol.pointSize * 1.6));
      return (
        <span style={box} className="flex flex-shrink-0 items-center justify-center">
          <span
            aria-hidden="true"
            dangerouslySetInnerHTML={{
              __html: buildSymbolSvg({
                path: definition.path,
                size,
                color: symbol.color,
                outlineColor: symbol.outlineColor,
                outlineWidth: symbol.outlineWidth,
                opacity: symbol.fillOpacity,
                rotation: symbol.rotation ?? 0,
              }),
            }}
          />
        </span>
      );
    }

    if (symbol.geometry === 'line') {
      return (
        <svg style={box} className="flex-shrink-0" aria-hidden="true">
          <line
            x1={2}
            y1={SWATCH_HEIGHT / 2}
            x2={SWATCH_WIDTH - 2}
            y2={SWATCH_HEIGHT / 2}
            stroke={symbol.color}
            strokeWidth={Math.max(1, Math.min(6, symbol.outlineWidth || 2))}
            strokeDasharray={getLineDashArray(symbol.lineStyle)}
            strokeOpacity={symbol.fillOpacity}
            strokeLinecap="round"
          />
        </svg>
      );
    }

    // Polygon, and anything whose geometry could not be determined — a filled
    // box with its outline is the least misleading thing to draw.
    return (
      <svg style={box} className="flex-shrink-0" aria-hidden="true">
        <rect
          x={1.5}
          y={1.5}
          width={SWATCH_WIDTH - 3}
          height={SWATCH_HEIGHT - 3}
          fill={symbol.fillStyle === 'transparent' ? 'none' : symbol.color}
          fillOpacity={symbol.fillOpacity}
          stroke={symbol.outlineColor}
          strokeWidth={Math.max(1, Math.min(4, symbol.outlineWidth || 1))}
          strokeDasharray={getLineDashArray(symbol.outlineStyle)}
        />
      </svg>
    );
  }

  /* Nothing could be resolved. An empty frame rather than a plausible blue
     box: inventing a symbol tells the reader something false and gives them no
     way to find out it was a guess. */
  return (
    <span
      style={box}
      aria-hidden="true"
      className="flex-shrink-0 rounded border border-dashed border-slate-300"
    />
  );
}

export default function LegendPanel({
  layers,
  geoServerUrl,
  arcgisBaseUrl,
  displayName,
  /** Layers switched on but outside their scale band, so the tab can say so. */
  hiddenByZoom,
}: {
  layers: Layer[];
  geoServerUrl: string;
  arcgisBaseUrl: string;
  displayName: (layer: Layer) => string;
  hiddenByZoom: number;
}) {
  const [groups, setGroups] = useState<LegendGroup[]>([]);
  const [loading, setLoading] = useState(false);

  /* Keyed on what actually changes a symbol, not on the layer objects: the
     array is rebuilt on every pan and zoom, and refetching an ArcGIS legend
     document each time would hammer the service for an answer that has not
     changed. */
  const signature = useMemo(
    () =>
      layers
        .map((layer) =>
          [
            layer.id,
            displayName(layer),
            layer.arcgisLayerId ?? '',
            layer.geometryType ?? '',
            layer.renderer ? JSON.stringify(layer.renderer) : '',
          ].join('|'),
        )
        .join('~'),
    [layers, displayName],
  );

  useEffect(() => {
    let active = true;

    if (layers.length === 0) {
      setGroups([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    void collectLegend(layers, {
      arcgisBaseUrl: arcgisBaseUrl || undefined,
      geoServerUrl: geoServerUrl || undefined,
      displayName,
    })
      .then((result) => {
        if (active) setGroups(result);
      })
      .catch((error: unknown) => {
        // A legend that cannot be built must not take the panel down with it.
        console.error('[legend] could not be built', error);
        if (active) setGroups([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, geoServerUrl, arcgisBaseUrl]);

  if (layers.length === 0) {
    return (
      <div className="px-4 py-6 text-center">
        <p className="text-sm text-slate-400">
          {hiddenByZoom > 0 ? 'Nothing is drawn at this zoom level' : 'No layers are switched on'}
        </p>
        {hiddenByZoom > 0 && (
          <p className="mt-1 text-xs text-slate-400">
            {hiddenByZoom} layer{hiddenByZoom === 1 ? '' : 's'} outside{' '}
            {hiddenByZoom === 1 ? 'its' : 'their'} visible scale range.
          </p>
        )}
      </div>
    );
  }

  return (
    <div>
      {loading && groups.length === 0 && (
        <p className="flex items-center gap-2 px-4 py-3 text-xs text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Building legend…
        </p>
      )}

      {groups.map((group, index) => (
        <div key={`${group.name}-${index}`} className="border-b border-slate-100 px-4 py-3">
          <p className="text-sm font-medium text-slate-700">{group.name}</p>

          <div className="mt-1.5 space-y-1.5">
            {group.swatches.map((swatch, swatchIndex) => (
              <div key={`${swatch.label}-${swatchIndex}`} className="flex items-center gap-2.5">
                <SwatchView swatch={swatch} />
                {/* A single-symbol layer has no class name; its title above is
                    the label, so an empty row would just add whitespace. */}
                {swatch.label && (
                  <span className="min-w-0 flex-1 truncate text-xs text-slate-600">
                    {swatch.label}
                  </span>
                )}
              </div>
            ))}
          </div>

          {group.hiddenClasses ? (
            <p className="mt-1.5 text-[11px] text-slate-400">
              +{group.hiddenClasses} more class{group.hiddenClasses === 1 ? '' : 'es'}
            </p>
          ) : null}
          {group.note && <p className="mt-1.5 text-[11px] text-amber-700">{group.note}</p>}
        </div>
      ))}

      {hiddenByZoom > 0 && (
        <p className="px-4 py-3 text-[11px] text-slate-400">
          {hiddenByZoom} more layer{hiddenByZoom === 1 ? '' : 's'} switched on but outside{' '}
          {hiddenByZoom === 1 ? 'its' : 'their'} visible scale range.
        </p>
      )}
    </div>
  );
}
